import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { WINDOW_LABEL, formatEuros, type AdminKpi, type LabeledCount } from './adminKpi';

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function kpiCard(label: string, value: string | number): string {
  return `<div class="card"><div class="v">${esc(String(value))}</div><div class="l">${esc(label)}</div></div>`;
}

// Tableau clé → valeur (distributions, séries). Vide → mention explicite (jamais un tableau vide muet).
function table(heading: string, items: { label: string; value: string | number }[]): string {
  if (items.length === 0) {
    return `<h3>${esc(heading)}</h3><p class="empty">Aucune donnée.</p>`;
  }
  const rows = items
    .map((i) => `<tr><td>${esc(i.label)}</td><td class="n">${esc(String(i.value))}</td></tr>`)
    .join('');
  return `<h3>${esc(heading)}</h3><table>${rows}</table>`;
}

function counts(heading: string, items: LabeledCount[]): string {
  return table(heading, items.map((i) => ({ label: i.label, value: i.count })));
}

// HTML A4 propre du dashboard — fonction PURE (testable), aucun accès natif.
export function buildAdminHtml(kpi: AdminKpi): string {
  const u = kpi.users;
  const usersSection = `
    <section>
      <h2>Utilisateurs</h2>
      <div class="cards">
        ${kpiCard('Inscrits (total)', u.total)}
        ${kpiCard('Nouveaux (fenêtre)', u.newInWindow)}
        ${kpiCard('Actifs (réels)', u.active)}
      </div>
      ${counts('Inscriptions par mois', u.signupSeries.map((p) => ({ label: p.month, count: p.count })))}
      ${counts('Tranches d’âge', u.byAge)}
      ${counts('Niveau d’études', u.byEducation)}
      ${counts('Villes', u.byCity)}
      ${counts('Domaines email', u.byDomain)}
    </section>`;

  const r = kpi.retention;
  const retentionSection = `
    <section>
      <h2>Rétention</h2>
      <div class="cards">
        ${kpiCard('DAU', r.dau)}
        ${kpiCard('WAU', r.wau)}
        ${kpiCard('MAU', r.mau)}
        ${kpiCard('Stickiness', `${r.stickiness}%`)}
        ${kpiCard('Jours actifs / user', r.avgActiveDays)}
      </div>
      ${table(
        'Cohortes (semaine d’inscription → % actifs J+1..J+7)',
        r.cohorts.map((c) => ({ label: `${c.week} (${c.size})`, value: `${c.w1Retention}%` })),
      )}
    </section>`;

  const usageSection = `
    <section>
      <h2>Usage</h2>
      <div class="cards">${kpiCard('Score ATS moyen', kpi.usage.atsAvg ?? 'n/a')}</div>
      ${counts('Volume par fonctionnalité', kpi.usage.byFeature)}
      ${counts('Distribution des scores ATS', kpi.usage.atsDistribution)}
    </section>`;

  const c = kpi.revenue.coaching;
  const s = kpi.revenue.subscriptions;
  const revenueSection = `
    <section>
      <h2>Revenus</h2>
      <div class="cards">
        ${kpiCard('Réservations coaching', c.count)}
        ${kpiCard('CA coaching', formatEuros(c.revenue))}
        ${kpiCard('MRR abonnements', formatEuros(s.mrr))}
      </div>
      ${counts('Coaching par formule', c.byFormule)}
      ${counts('Abonnements par plan', s.byPlan)}
    </section>`;

  const errorRows = kpi.errors
    .map(
      (e) =>
        `<tr><td>${esc(e.feature)}</td><td>${esc(e.errorType)}</td><td class="n">${e.count}</td><td class="n">${e.distinctUsers}</td></tr>`,
    )
    .join('');
  const errorsSection = `
    <section>
      <h2>Erreurs (top 5)</h2>
      ${
        kpi.errors.length === 0
          ? '<p class="empty">Aucune erreur sur la période.</p>'
          : `<table><tr><th>Fonctionnalité</th><th>Type</th><th class="n">Occurrences</th><th class="n">Utilisateurs</th></tr>${errorRows}</table>`
      }
    </section>`;

  const schoolRows = kpi.schools
    .map(
      (r) =>
        `<tr><td>${esc(r.domain)}</td><td class="n">${r.students}</td><td class="n">${r.active}</td><td class="n">${r.cvsCreated}</td><td class="n">${r.atsAvg ?? 'n/a'}</td><td class="n">${r.interviews}</td></tr>`,
    )
    .join('');
  const schoolsSection = `
    <section>
      <h2>Reporting école</h2>
      ${
        kpi.schools.length === 0
          ? '<p class="empty">Aucun établissement identifié (domaines grand public exclus).</p>'
          : `<table><tr><th>Établissement</th><th class="n">Élèves</th><th class="n">Actifs</th><th class="n">CV</th><th class="n">ATS moy.</th><th class="n">Entretiens</th></tr>${schoolRows}</table>`
      }
    </section>`;

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8" /><style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #071B4D; font-size: 10pt; }
  header { border-bottom: 2px solid #578DEA; padding-bottom: 8px; margin-bottom: 12px; }
  h1 { font-size: 16pt; margin: 0; }
  .meta { color: #5B6475; font-size: 9pt; margin-top: 2px; }
  h2 { font-size: 12pt; color: #578DEA; margin: 16px 0 6px; border-bottom: 1px solid #E1E6EF; padding-bottom: 3px; }
  h3 { font-size: 10pt; margin: 10px 0 4px; color: #071B4D; }
  .cards { display: flex; gap: 8px; flex-wrap: wrap; }
  .card { border: 1px solid #E1E6EF; border-radius: 8px; padding: 8px 12px; min-width: 120px; }
  .card .v { font-size: 15pt; font-weight: 700; }
  .card .l { font-size: 8pt; color: #5B6475; }
  table { width: 100%; border-collapse: collapse; margin: 4px 0; }
  td, th { border-bottom: 1px solid #EDF0F6; padding: 3px 6px; text-align: left; font-size: 9pt; }
  th { color: #5B6475; font-weight: 600; }
  .n { text-align: right; }
  .empty { color: #9AA3B4; font-size: 9pt; }
  footer { margin-top: 16px; color: #9AA3B4; font-size: 7.5pt; line-height: 1.4; }
</style></head>
<body>
  <header>
    <h1>Horméia — Dashboard admin</h1>
    <div class="meta">Période : ${esc(WINDOW_LABEL[kpi.window])} · Généré le ${esc(kpi.generatedAt)}</div>
  </header>
  ${usersSection}
  ${retentionSection}
  ${usageSection}
  ${revenueSection}
  ${errorsSection}
  ${schoolsSection}
  <footer>
    Rétention basée sur les ouvertures réelles de l'app (1/jour) : se remplit à partir du déploiement.
    Les distributions (âge, études, ville, domaine, plan) reflètent l'état de la base, non filtré par période.
    Niveau d'études et ville sont déduits par l'IA à l'analyse d'un CV (couverture partielle,
    « Non déterminé » si non inférable). MRR estimé à partir des tarifs catalogue.
  </footer>
</body></html>`;
}

// Génère le PDF puis ouvre la feuille de partage native.
export async function exportAdminPdf(kpi: AdminKpi): Promise<void> {
  const { uri } = await Print.printToFileAsync({ html: buildAdminHtml(kpi) });
  const available = await Sharing.isAvailableAsync();
  if (!available) throw new Error('Le partage n’est pas disponible sur cet appareil.');
  await Sharing.shareAsync(uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf' });
}
